import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts";

// Connects to data-controller="charts"
export default class extends Controller {
  static values = { revenueData: Object, subscribersData: Array, usersData: Array, viewsData: Object, workoutsData: Object };
  revenueChart = null;
  subscribersChart = null;
  viewsChart = null;
  workoutsData = null;
  usersData = null;

  connect() {
    this.renderCharts();
  }

  renderCharts() {
    if (this.hasRevenueDataValue) {
      this.renderRevenueChart();
    }
    if (this.hasSubscribersDataValue) {
      this.renderSubscribersChart();
    }
    if (this.hasUsersDataValue) {
      this.renderUsersChart();
    }
    if (this.hasViewsDataValue) {
      this.renderViewsChart();
    }
    if (this.hasWorkoutsDataValue) {
      this.renderWorkoutsChart();
    }
  }

  renderRevenueChart() {
    if (this.revenueChart) this.revenueChart.destroy(); // Destroy existing chart

    const options = {
      chart: { type: "bar", height: 350 },
      plotOptions: { bar: { borderRadius: 10, columnWidth: "20" } },
      xaxis: { categories: Object.keys(this.revenueDataValue) },
      yaxis: { title: { text: "Revenue ($)" }, tickAmount: 5 },
      grid: { show: false },
      dataLabels: { enabled: false },
      colors: ["#71A2F6"],
      series: [{ name: "Revenue", data: Object.values(this.revenueDataValue) }],
    };

    this.revenueChart = new ApexCharts(document.getElementById("revenue-chart"), options);
    this.revenueChart.render();
  }

  renderWorkoutsChart() {
    if (this.workoutsChart) this.workoutsChart.destroy(); // Destroy existing chart

    const options = {
      chart: { type: "area", height: 400 },
      xaxis: { categories: Object.keys(this.workoutsDataValue), labels: { style: { fontSize: "17px" } } },
      yaxis: { title: { text: "Total Views" }, tickAmount: 5 },
      grid: { show: true },
      dataLabels: { enabled: false },
      colors: ["#268BF5"],
      series: [{ name: "Total Views", data: Object.values(this.workoutsDataValue) }],
    };

    this.workoutsChart = new ApexCharts(document.getElementById("workouts-chart"), options);
    this.workoutsChart.render();
  }

  renderViewsChart() {
    if (this.viewsChart) this.viewsChart.destroy(); // Destroy existing chart

    const options = {
      chart: { type: 'bar', height: 350, width: '100%',
        toolbar: { tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true } },
        animations: { enabled: true }
      },
      plotOptions: { bar: { borderRadius: 10, columnWidth: '60%', distributed: true }, },
      xaxis: { categories: Object.keys(this.viewsDataValue),
        labels: { rotate: -45, style: { fontSize: '12px' }, trim: true, hideOverlappingLabels: false },
        tickPlacement: 'on', axisTicks: { show: false },
      },
      yaxis: { title: { text: 'Minutes Viewed' }, tickAmount: 5 },
      grid: { show: false },
      dataLabels: { enabled: false },
      colors: ['#71A2F6'],
      series: [{ name: 'Minutes Viewed', data: Object.values(this.viewsDataValue) }],
      responsive: [{ breakpoint: 1000,
        options: { plotOptions: { bar: { columnWidth: '80%' } }, xaxis: { labels: { rotate: -90 } } }
      }],
    };

    // Create container with horizontal scroll
    const chartContainer = document.getElementById('views-chart');
    chartContainer.style.overflowX = 'auto';
    chartContainer.style.width = '100%';

    // Create inner container for the chart
    const innerContainer = document.createElement('div');
    innerContainer.style.minWidth = `${Object.keys(this.viewsDataValue).length * 50}px`;
    chartContainer.appendChild(innerContainer);

    // Render chart
    this.viewsChart = new ApexCharts(innerContainer, options);
    this.viewsChart.render();
  }

  renderUsersChart() {
    if (this.usersChart) this.usersChart.destroy(); // Destroy existing chart

    const categories = Object.keys(this.usersDataValue[0].data);
    const series = this.usersDataValue.map(entry => ({
      name: entry.name,
      data: Object.values(entry.data),
    }));

    const options = {
      chart: { type: "bar", height: 300, stacked: true },
      plotOptions: { bar: { borderRadius: 10, columnWidth: "20" } },
      xaxis: { categories: categories },
      yaxis: { title: { text: "users" }, tickAmount: 5 },
      grid: { show: false },
      dataLabels: { enabled: false },
      legend: { onItemClick: { toggleDataSeries: false } }, 
      colors: ["#71A2F6", "#C8DDF5", "#EB5757"],
      series: series,
    };

    this.usersChart = new ApexCharts(document.getElementById("users-chart"), options);
    this.usersChart.render();
  }

  renderSubscribersChart() {
    if (this.subscribersChart) this.subscribersChart.destroy(); // Destroy existing chart

    const categories = Object.keys(this.subscribersDataValue[0].data);
    const series = this.subscribersDataValue.map(entry => ({
      name: entry.name,
      data: Object.values(entry.data),
    }));

    const colors = this.subscribersDataValue.length === 5
      ? ["#71A2F6", "#C8DDF5", "#F2C94C", "#4CAF50", "#F44336"]
      : this.subscribersDataValue.length === 4
        ? ["#C8DDF5", "#F2C94C", "#27AE60", "#EB5757"]
        : ["#71A2F6", "#C8DDF5", "#F2C94C", "#4CAF50", "#F44336"];

    const options = {
      chart: { type: "bar", height: 350, stacked: true },
      plotOptions: { bar: { borderRadius: 10, columnWidth: "20" } },
      xaxis: { categories: categories },
      yaxis: { title: { text: "Subscribers" }, tickAmount: 5 },
      grid: { show: false },
      dataLabels: { enabled: false },
      legend: { onItemClick: { toggleDataSeries: false } }, 
      colors: colors,
      series: series,
    };

    this.subscribersChart = new ApexCharts(document.getElementById("subscribers-chart"), options);
    this.subscribersChart.render();
  }

  update(event) {
    const { revenueData, subscribersData, viewsData, workoutsData, usersData } = event.detail;

    if (revenueData) {
      this.revenueDataValue = revenueData;
      this.renderRevenueChart();
    }

    if (viewsData) {
      this.viewsDataValue = viewsData;
      this.renderViewsChart();
    }

    if (workoutsData) {
      this.workoutsDataValue = workoutsData;
      this.renderWorkoutsChart();
    }

    if (subscribersData) {
      this.subscribersDataValue = subscribersData;
      this.renderSubscribersChart();
    }

    if (usersData) {
      this.usersDataValue = usersData;
      this.renderUsersChart();
    }
  }
}
